import React, { useState, useEffect } from 'react';
import api from '../services/api';

function BankStatements({ clientId, isClosed }) {
  const [releves, setReleves] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [selectedReleve, setSelectedReleve] = useState(null);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [activeTab, setActiveTab] = useState('current'); // 'current' or 'archive'
  const [showAllTransactions, setShowAllTransactions] = useState(false);
  const [uploadingId, setUploadingId] = useState(null);

  useEffect(() => {
    loadReleves();
  }, [clientId]);

  useEffect(() => {
    loadTransactions();
  }, [clientId, selectedReleve]);

  const loadReleves = async () => {
    try {
      const data = await api.get(`/bank/${clientId}/releves`);
      if (data && data.success) {
        setReleves(data.releves);
      }
    } catch (error) {
      console.error('Erreur chargement relevés:', error);
    }
  };

  const loadTransactions = async () => {
    try {
      setLoading(true);
      const url = selectedReleve 
        ? `/bank/${clientId}/transactions?releve_id=${selectedReleve}`
        : `/bank/${clientId}/transactions`;
      
      const data = await api.get(url);
      if (data && data.success) {
        setTransactions(data.transactions);
      }
    } catch (error) {
      console.error('Erreur chargement transactions:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleFileUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    try {
      setUploading(true);
      const formData = new FormData();
      formData.append('file', file);
      const response = await api.upload(`/bank/${clientId}/upload`, formData);

      if (response && response.success) {
        alert('✅ Relevé importé avec succès');
        await loadReleves();
        await loadTransactions();
      } else {
        alert(`❌ Erreur : ${response.error || 'Erreur lors de l\'import'}`);
      }
    } catch (error) {
      console.error('Erreur upload:', error);
      alert('❌ Erreur réseau lors de l\'upload');
    } finally {
      setUploading(false);
    }
  };

  const handleDeleteReleve = async (id, nom) => {
    if (isClosed) return;
    if (!window.confirm(`⚠️ Supprimer le relevé "${nom}" et TOUTES ses transactions ? Cette action est irréversible.`)) return;

    try {
      const data = await api.delete(`/bank/${clientId}/releves/${id}`);
      if (data.success) {
        if (selectedReleve === id) setSelectedReleve(null);
        await loadReleves();
        await loadTransactions();
      }
    } catch (error) {
      console.error('Erreur suppression relevé:', error);
    }
  };

  const handleRapprochement = async () => {
    if (isClosed) return;
    try {
      const data = await api.post(`/bank/${clientId}/rapprochement`, {});
      if (data.success) {
        alert(data.message);
        await loadTransactions();
      }
    } catch (error) {
      alert('Erreur lors du rapprochement');
    }
  };

  const handleDirectUpload = async (event, transactionId) => {
    const file = event.target.files[0];
    if (!file) return;

    try {
      setUploadingId(transactionId);
      const formData = new FormData();
      formData.append('file', file);
      const response = await api.upload(`/bank/${clientId}/transactions/${transactionId}/upload`, formData);

      if (response && response.success) {
        alert('✅ Justificatif associé et rapprochement effectué !');
        await loadTransactions();
      } else {
        alert(`❌ Erreur : ${response.error || 'Erreur lors du traitement'}`);
      }
    } catch (error) {
      console.error('Erreur upload direct:', error);
      alert('❌ Erreur réseau lors de l\'upload');
    } finally {
      setUploadingId(null);
    }
  };

  // Filtrage des relevés par onglet (En cours vs Archivés)
  const filteredReleves = releves.filter(r => {
    // Si toutes les transactions ne sont pas rapprochées, le relevé est toujours en cours
    const isCurrent = (r.nb_rapproches || 0) < r.nb_transactions;
    return activeTab === 'current' ? isCurrent : !isCurrent;
  });

  // Filtrage des transactions (masquer rapprochées si demandé)
  const filteredTransactions = showAllTransactions 
    ? transactions 
    : transactions.filter(t => !t.rapproche);

  const styles = {
    container: { background: 'white', padding: '24px', borderRadius: '12px', border: '1px solid #E8E7E3' },
    header: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px', flexWrap: 'wrap', gap: '16px' },
    tabs: { display: 'flex', gap: '24px', marginBottom: '20px', borderBottom: '1px solid #E2E8F0' },
    tab: (active) => ({
      padding: '8px 16px', cursor: 'pointer', fontWeight: '600', fontSize: '14px',
      color: active ? '#2D3748' : '#718096', borderBottom: active ? '2px solid #2D3748' : '2px solid transparent',
      transition: 'all 0.2s'
    }),
    relevesGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: '16px', marginBottom: '32px' },
    releveCard: (isSelected) => ({
      padding: '16px', borderRadius: '12px', border: isSelected ? '2px solid #2D3748' : '1px solid #E2E8F0',
      background: isSelected ? '#F7FAFC' : 'white', cursor: 'pointer', transition: 'all 0.2s',
      position: 'relative'
    }),
    cardHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '8px' },
    cardTitle: { fontWeight: '600', fontSize: '14px', color: '#2D3748', maxWidth: '85%', overflow: 'hidden', textOverflow: 'ellipsis' },
    cardMeta: { fontSize: '12px', color: '#718096' },
    deleteIcon: { 
      fontSize: '18px',
      cursor: 'pointer', 
      padding: '4px',
      borderRadius: '4px',
      transition: 'background 0.2s',
      '&:hover': { background: '#FFF5F5' }
    },
    transactionCard: {
      padding: '12px 16px', borderRadius: '8px', border: '1px solid #E2E8F0',
      display: 'grid', gridTemplateColumns: '100px 1fr 120px 120px 150px', gap: '16px', alignItems: 'center', marginBottom: '8px'
    },
    badge: { padding: '4px 12px', borderRadius: '12px', fontSize: '11px', fontWeight: '600', textAlign: 'center' },
    amount: (val) => ({ fontWeight: '600', color: val >= 0 ? '#48BB78' : '#F56565', textAlign: 'right' }),
    button: { padding: '10px 16px', borderRadius: '8px', border: 'none', cursor: 'pointer', fontSize: '14px', fontWeight: '500', display: 'flex', alignItems: 'center', gap: '8px' },
    primaryButton: { background: '#2D3748', color: 'white' },
    secondaryButton: { background: '#EDF2F7', color: '#2D3748' }
  };

  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <h2 style={{ fontSize: '20px', fontWeight: '600' }}>Banque & Rapprochement</h2>
        <div style={{ display: 'flex', gap: '12px' }}>
          <input type="file" accept=".pdf" onChange={handleFileUpload} disabled={uploading || isClosed} style={{ display: 'none' }} id="bank-upload" />
          <label htmlFor="bank-upload" style={{ ...styles.button, ...styles.primaryButton, cursor: isClosed ? 'not-allowed' : 'pointer', opacity: isClosed ? 0.6 : 1 }}>
            {uploading ? '⏳ Import...' : '📄 Importer Relevé'}
          </label>
          <button onClick={handleRapprochement} disabled={transactions.length === 0 || isClosed} style={{ ...styles.button, ...styles.secondaryButton, cursor: isClosed ? 'not-allowed' : 'pointer', opacity: isClosed ? 0.6 : 1 }}>
            🔄 Rapprocher tout
          </button>
        </div>
      </div>

      <div style={styles.tabs}>
        <div style={styles.tab(activeTab === 'current')} onClick={() => setActiveTab('current')}>En cours</div>
        <div style={styles.tab(activeTab === 'archive')} onClick={() => setActiveTab('archive')}>Archives</div>
      </div>

      <div style={styles.relevesGrid}>
        {filteredReleves.length === 0 ? (
          <div style={{ padding: '20px', color: '#718096', fontSize: '14px' }}>Aucun relevé dans cette section</div>
        ) : filteredReleves.map(r => (
          <div key={r.id} style={styles.releveCard(selectedReleve === r.id)} onClick={() => setSelectedReleve(r.id)}>
            <div style={styles.cardHeader}>
              <div style={styles.cardTitle} title={r.nom_fichier}>
                {r.nom_fichier} {!r.iban_verifie && '⚠️'}
              </div>
              {!isClosed && (
                <button 
                  onClick={(e) => { e.stopPropagation(); handleDeleteReleve(r.id, r.nom_fichier); }} 
                  style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '4px', fontSize: '16px' }}
                  title="Supprimer ce relevé et toutes ses transactions ? Cette action est irréversible."
                >
                  🗑️
                </button>
              )}
            </div>
            <div style={styles.cardMeta}>
              📅 {new Date(r.periode_debut).toLocaleDateString()} - {new Date(r.periode_fin).toLocaleDateString()}<br/>
              📊 {r.nb_transactions} transactions | Fin: {r.solde_final}€
            </div>
          </div>
        ))}
      </div>

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
        <h3 style={{ fontSize: '16px', fontWeight: '600' }}>
          {selectedReleve ? 'Transactions du relevé' : 'Toutes les transactions non rapprochées'}
        </h3>
        <button 
          onClick={() => setShowAllTransactions(!showAllTransactions)}
          style={{ background: 'none', border: 'none', color: '#3182CE', cursor: 'pointer', fontSize: '13px' }}
        >
          {showAllTransactions ? 'Masquer rapprochées' : 'Voir tout (inclure rapprochées)'}
        </button>
      </div>

      {loading ? <div style={{textAlign: 'center', padding: '20px'}}>Chargement...</div> : (
        <div>
          {filteredTransactions.length === 0 && <div style={{ textAlign: 'center', padding: '40px', color: '#718096' }}>Aucune transaction à afficher</div>}
          {filteredTransactions.map(t => (
            <div key={t.id} style={styles.transactionCard}>
              <div style={{ fontSize: '13px' }}>{new Date(t.date).toLocaleDateString()}</div>
              <div style={{ fontSize: '13px', fontWeight: '500', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{t.libelle}</div>
              <div style={styles.amount(t.montant)}>{t.type === 'CREDIT' ? '+' : '-'}{Math.abs(t.montant).toFixed(2)} €</div>
              <div style={{ ...styles.badge, background: t.rapproche ? '#C6F6D5' : '#FEEBC8', color: t.rapproche ? '#22543D' : '#7B341E' }}>
                {t.rapproche ? 'Rapproché' : 'Non rapproché'}
              </div>
              <div style={{ display: 'flex', justifyContent: 'center' }}>
                {!t.rapproche ? (
                  <div>
                    <input 
                      type="file" 
                      id={`direct-upload-${t.id}`} 
                      style={{ display: 'none' }} 
                      onChange={(e) => handleDirectUpload(e, t.id)} 
                      disabled={uploadingId === t.id || isClosed}
                    />
                    <label 
                      htmlFor={`direct-upload-${t.id}`}
                      style={{
                        padding: '6px 12px',
                        borderRadius: '6px',
                        background: isClosed ? '#A0AEC0' : '#3182CE',
                        color: 'white',
                        fontSize: '11px',
                        fontWeight: '600',
                        cursor: isClosed ? 'not-allowed' : 'pointer',
                        textAlign: 'center',
                        display: 'inline-block',
                        transition: 'background 0.2s'
                      }}
                    >
                      {uploadingId === t.id ? '⏳ ...' : '📁 Joindre Reçu'}
                    </label>
                  </div>
                ) : (
                  <div style={{ color: '#48BB78', fontSize: '12px', fontWeight: '600', display: 'flex', alignItems: 'center', gap: '4px' }}>
                    <span>✓</span> Associé
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default BankStatements;
